/*
Copyright© Celligence International, LLC - 2026

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Box, Button } from '@mui/material';
import { useCallback, useRef } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNavigationAlert } from '../../contexts/NavigationAlertContext';
import axiosCall from '../../services/axios';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { useProctorImportReportsContext } from '../../contexts/ProctorImportReportsContext';

const ProctorImportReportsForm = () => {
  const { token } = useAuthContext();
  const { setAlert, setStatus } = useNavigationAlert();
  const { processing, setProcessing } = useProctorImportReportsContext();

  const alertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showProctorAlert = useCallback(
    (message: string, status: 'success' | 'error') => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }

      setStatus(status);
      setAlert(message);

      alertTimerRef.current = setTimeout(() => {
        setAlert(null);
        setStatus(null);
        alertTimerRef.current = null;
      }, 15000);
    },
    [setAlert, setStatus],
  );

  const submitReports = useCallback(async () => {
    if (processing) {
      return;
    }

    const config = {};

    const headers = {
      'Session-Token': token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    try {
      setProcessing(true);

      showProctorAlert(
        'The Proctor report request has been submitted and is being processed. ' +
          'You may continue using RedCarpet. A status update will be displayed when processing is complete.',
        'success',
      );

      const response = await axiosCall(
        {
          url: 'live/servicing/proctor/generate',
          method: 'POST',
          data: {
            fileType: 'ALL',
          },
        },
        config,
        headers,
      );

      if (!response) {
        throw new Error('No response received from Proctor report generation.');
      }

      let result = response.result;
      if (typeof result === 'string') {
        try {
          result = JSON.parse(result);
        } catch (parseError) {
          // Keep the original string if the API ever returns plain text
          console.debug('Proctor response result is not JSON:', parseError);
        }
      }

      // API may return HTTP 200 for both validation errors and successful submissions,
      // So determine the result from the returned status fields
      if (result && typeof result === 'object') {
        const fileGenerationStatus = String(result['FileGenerationStatus'] ?? '').toLowerCase();
        const sftpStatus = String(result['SFTPStatus'] ?? '').toLowerCase();

        // Validation errors were found in the generated reports.
        if (fileGenerationStatus === 'false') {
          showProctorAlert(
            'Errors were found in the Proctor reports and the reports were not submittied. ' +
              'Please review the error files uploaded to Company Imaging, correct the reported issues in SunSoft or RedCarpet as applicable, ' +
              'and submit the reports again.',
            'error',
          );

          return;
        }

        // Reports were generated but failed to upload to Proctor SFTP
        if (sftpStatus === 'false') {
          showProctorAlert(
            'The Proctor reports could not be uploaded to SFTP. Please try again. If the issue conitnues, contact support.',
            'error',
          );

          return;
        }

        // Reports were successfully submitted to Proctor SFTP
        if (fileGenerationStatus === 'true' && sftpStatus === 'true') {
          showProctorAlert(
            'Proctor reports were submitted successfully. A confirmation email has been sent to the stakeholders.',
            'success',
          );

          return;
        }
      }

      // If the response contains neither failure nor success responses, throw an exception
      throw new Error('Unable to process Proctor reports.');
    } catch (error) {
      // Technical/API failure
      console.error('Error submitting Proctor reports:', error);
      showProctorAlert(
        'Unable to submit Proctor reports. Please try again. If the issue continues, contact support.',
        'error',
      );
    } finally {
      setProcessing(false);
    }
  }, [token, processing, setProcessing, setAlert, setStatus]);

  return (
    <EnvelopeWithBox blockName='Proctor Daily Import Files'>
      <Box mt={1}>
        <Button variant='contained' type='button' disabled={processing} onClick={submitReports}>
          {processing ? 'Processing...' : 'Submit Reports To Proctor'}
        </Button>
      </Box>
    </EnvelopeWithBox>
  );
};

export default ProctorImportReportsForm;
